import { CalendarIcon, InfoTooltip, ProCard } from '@components'
import TrendPill from '@dashboard/components/TrendPill'
import { calcTrendDelta } from '@dashboard/utils/analyticsHelpers'
import { buildLinePath, buildYAxisTicks } from '@dashboard/utils/svgHelpers'
import { format, subDays } from '@integrations/calendar'
import cn from '@integrations/classnames'
import { Link } from '@integrations/routing'
import { paths, useGetAggregatedBalanceQuery, useGraphQry } from 'mmfintech-backend-api'
import { formatMoney, tr } from 'mmfintech-commons'
import skeletonStyles from '../DashboardSkeleton/DashboardSkeleton.module.scss'
import styles from './BalanceAnalyticsChart.module.scss'

const SVG_WIDTH = 1000
const SVG_HEIGHT = 300

interface Props {
  balance?: { amount?: number; currencyCode?: string } | null
}

function formatPeriodRange(): string {
  const today = new Date()
  const start = subDays(today, 6)
  const startMonth = format(start, 'MMM')
  const endMonth = format(today, 'MMM')
  const year = format(today, 'yyyy')
  if (startMonth === endMonth) {
    return `${startMonth} ${format(start, 'd')} – ${format(today, 'd')}, ${year}`
  }
  return `${format(start, 'MMM d')} – ${format(today, 'MMM d')}, ${year}`
}

/** Splits "27,150.30 EUR" into its whole part, decimals and currency so each can be sized on its own. */
function splitAmount(balance: Props['balance']) {
  if (!balance || balance.amount == null) return null
  const formatted = formatMoney(balance.amount, balance.currencyCode ?? '')
  const currency = balance.currencyCode ?? ''
  const numeric = currency && formatted.endsWith(currency) ? formatted.slice(0, -currency.length).trim() : formatted
  const match = numeric.match(/^(.*?)([.,]\d{1,2})$/)
  return { whole: match ? match[1] : numeric, decimals: match ? match[2] : '', currency }
}

function BalanceAnalyticsChart({ balance }: Props) {
  const { chartData, chartLabels } = useGraphQry()

  // Previous 7-day period for dashed comparison line
  const { data: prevData } = useGetAggregatedBalanceQuery({
    dateFrom: format(subDays(new Date(), 13), 'yyyy-MM-dd'),
    dateTo: format(subDays(new Date(), 7), 'yyyy-MM-dd')
  })
  const prevChartData: number[] = Array.isArray(prevData) ? prevData.map((e: { amount: number }) => e.amount) : []

  const allValues = [...(chartData ?? []), ...prevChartData].filter((v): v is number => typeof v === 'number')
  const valueMax = allValues.length ? Math.max(...allValues) : 0
  const valueMin = 0

  const currentPath = buildLinePath(chartData ?? [], SVG_WIDTH, SVG_HEIGHT, valueMin, valueMax)
  const previousPath = buildLinePath(prevChartData, SVG_WIDTH, SVG_HEIGHT, valueMin, valueMax)
  const yTicks = buildYAxisTicks(valueMax)

  const isEmpty = !chartData?.length
  const amount = splitAmount(balance)

  // Period-over-period movement of the closing balance, so the headline states the direction the
  // line is going instead of leaving the reader to eyeball a 7-point curve.
  const closing = isEmpty ? 0 : chartData[chartData.length - 1]
  const prevClosing = prevChartData.length ? prevChartData[prevChartData.length - 1] : 0
  const delta = isEmpty ? null : calcTrendDelta(closing, prevClosing)

  // The SVG is stretched with preserveAspectRatio='none', which would squash a circle marker into
  // an ellipse — the end-of-line dot is positioned as an overlay against the same 0..valueMax
  // scale instead.
  const range = valueMax - valueMin || 1
  const closingOffset = (1 - (closing - valueMin) / range) * 100

  const header = (
    <div className={cn(styles['balance-chart-card__header'], 'balance-chart-card__header')}>
      <div className={cn(styles['balance-chart-card__heading'], 'balance-chart-card__heading')}>
        <div className={cn(styles['balance-chart-card__title-row'], 'balance-chart-card__title-row')}>
          <h3 className={cn(styles['balance-chart-card__title'], 'balance-chart-card__title')}>
            <span className={cn(styles['balance-chart-card__title-swatch'], 'balance-chart-card__title-swatch')} />
            {tr('FRONTEND.DASHBOARD.KPI.TOTAL_BALANCE', 'Total balance')}
          </h3>
          <InfoTooltip
            variant='outline'
            label={tr('FRONTEND.DASHBOARD.KPI.TOTAL_BALANCE', 'Total balance')}
            triggerLabel={tr('FRONTEND.DASHBOARD.KPI.INFO_LABEL', 'More about {{METRIC}}', {
              METRIC: tr('FRONTEND.DASHBOARD.KPI.TOTAL_BALANCE', 'Total balance')
            })}
            content={tr(
              'FRONTEND.DASHBOARD.KPI.TOTAL_BALANCE_INFO',
              'Combined balance of all your accounts, shown in your main currency.'
            )}
          />
        </div>
        <div className={cn(styles['balance-chart-card__amount-row'], 'balance-chart-card__amount-row')}>
          <span className={cn(styles['balance-chart-card__amount'], 'balance-chart-card__amount')}>
            {amount ? (
              <>
                {amount.whole}
                <span className={cn(styles['balance-chart-card__decimals'], 'balance-chart-card__decimals')}>
                  {amount.decimals}
                </span>
                <span className={cn(styles['balance-chart-card__currency'], 'balance-chart-card__currency')}>
                  {amount.currency}
                </span>
              </>
            ) : (
              '—'
            )}
          </span>
          {delta && <TrendPill delta={delta} />}
        </div>
      </div>

      <div className={cn(styles['balance-chart-card__aside'], 'balance-chart-card__aside')}>
        <div className={cn(styles['balance-chart-card__actions'], 'balance-chart-card__actions')}>
          <p className={cn(styles['balance-chart-card__period'], 'balance-chart-card__period')}>
            <CalendarIcon />
            {formatPeriodRange()}
          </p>
          <Link
            to={paths.banking.accounts.list()}
            className={cn(styles['balance-chart-card__all'], 'balance-chart-card__all')}
            data-test='all-accounts-link'
          >
            {tr('FRONTEND.DASHBOARD.KPI.ALL_ACCOUNTS', 'All Accounts')} ›
          </Link>
        </div>
        {!isEmpty && (
          <div className={cn(styles['balance-chart-card__legend'], 'balance-chart-card__legend')}>
            <span className={cn(styles['balance-chart-card__legend-item'], 'balance-chart-card__legend-item')}>
              <span
                className={cn(
                  styles['balance-chart-card__legend-swatch'],
                  'balance-chart-card__legend-swatch',
                  styles['balance-chart-card__legend-swatch--current'],
                  'balance-chart-card__legend-swatch--current'
                )}
              />
              {tr('FRONTEND.DASHBOARD.BALANCE_CHART.LAST_7_DAYS', 'Last 7 days')}
            </span>
            <span className={cn(styles['balance-chart-card__legend-item'], 'balance-chart-card__legend-item')}>
              <span
                className={cn(
                  styles['balance-chart-card__legend-swatch'],
                  'balance-chart-card__legend-swatch',
                  styles['balance-chart-card__legend-swatch--previous'],
                  'balance-chart-card__legend-swatch--previous'
                )}
              />
              {tr('FRONTEND.DASHBOARD.BALANCE_CHART.PREVIOUS_7_DAYS', 'Previous 7 days')}
            </span>
          </div>
        )}
      </div>
    </div>
  )

  if (isEmpty) {
    return (
      <ProCard tone='brand' className={cn(styles['balance-chart-card'], 'balance-chart-card')}>
        {header}
        <div
          className={cn(
            styles['balance-chart-card__skeleton'],
            skeletonStyles['dashboard-skeleton'],
            'balance-chart-card__skeleton'
          )}
        />
      </ProCard>
    )
  }

  return (
    <ProCard tone='brand' className={cn(styles['balance-chart-card'], 'balance-chart-card')}>
      {header}

      <div className={cn(styles['balance-chart-card__chart-area'], 'balance-chart-card__chart-area')}>
        {/* Y-axis labels — one per gridline, so the two stay aligned */}
        <div className={cn(styles['balance-chart-card__y-axis'], 'balance-chart-card__y-axis')}>
          {yTicks.map((tick, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: y-axis ticks are positional, never reordered
            <span key={i}>{tick}</span>
          ))}
        </div>

        {/* SVG chart — colors via CSS custom properties from BalanceAnalyticsChart.module.scss */}
        <div className={cn(styles['balance-chart-card__svg-wrapper'], 'balance-chart-card__svg-wrapper')}>
          <svg className={cn('w-full h-full')} viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`} preserveAspectRatio='none'>
            <defs>
              <linearGradient id='balanceGradient' x1='0%' y1='0%' x2='0%' y2='100%'>
                <stop offset='0%' stopColor='var(--chart-accent)' stopOpacity={0.16} />
                <stop offset='100%' stopColor='var(--chart-accent)' stopOpacity={0.08} />
              </linearGradient>
            </defs>

            {[0, 75, 150, 225, 300].map((y) => (
              <line key={y} x1='0' y1={y} x2={SVG_WIDTH} y2={y} stroke='var(--chart-grid)' strokeWidth='1' />
            ))}

            {currentPath && (
              <path d={`${currentPath} L${SVG_WIDTH},${SVG_HEIGHT} L0,${SVG_HEIGHT} Z`} fill='url(#balanceGradient)' />
            )}

            {previousPath && (
              <path
                d={previousPath}
                fill='none'
                stroke='var(--chart-prev)'
                strokeWidth='1.5'
                strokeDasharray='4,4'
                vectorEffect='non-scaling-stroke'
              />
            )}

            <path
              d={currentPath}
              fill='none'
              stroke='var(--chart-accent)'
              strokeWidth='2.5'
              strokeLinecap='round'
              vectorEffect='non-scaling-stroke'
            />
          </svg>

          <span
            className={cn(styles['balance-chart-card__endpoint'], 'balance-chart-card__endpoint')}
            style={{ top: `${closingOffset}%` }}
          />
        </div>
      </div>

      {/* X-axis labels */}
      <div className={cn(styles['balance-chart-card__x-axis'], 'balance-chart-card__x-axis')}>
        {(chartLabels ?? []).map((label) => (
          <span key={label}>{label}</span>
        ))}
      </div>
    </ProCard>
  )
}

export default BalanceAnalyticsChart
